// IMPORT REACT
import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import parse from "html-react-parser";

// IMPORT CSS
import "../../../theme/style.scss";
import "react-date-range/dist/styles.css"; // Main style file
import "react-date-range/dist/theme/default.css"; // Default theme CSS

// IMPORT UI COMPONENTS
import {
  Box,
  Grid,
  IconButton,
  InputAdornment,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Tooltip,
  Popover,
  Pagination,
  Stack,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogActions,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import AddIcon from "@mui/icons-material/Add";
import CustomBreadcrumb from "../../../components/BreadcrumbsComponent/CustomBreadcrumbs";
import { AlertType } from "../../../services/models/AlertType";
import SkeletonWrapper from "../../../components/Skeleton/SkeletonWrapper";

// IMPORT SERVICE
import { EncryptDecryptService } from "../../../services/common/EncryptionService";
import { PAGE_SIZE } from "../../../services/common/const";
import { PreferencesContext } from "../../../PreferenceContext";
import { CustomTextField } from "../../../components/CustomFields/TextField";

import {
  DeleteHistoryTemplate,
  GetHistoryTemplateList
} from "../../../services/HistoryTemplateService";

// SCHEMA
interface Data {
  historyTemplateId: number;
  code: string;
  authorizePersion: string;
  organization: string;
  designation: string;
  email: string;
  website: string;
  plan: string;
  none: string;
}

type Order = "asc" | "desc" | "";

// SCHEMA FOR TABLE
interface EnhancedTableProps {
  numSelected: number;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  order: Order;
  orderBy: String;
  rowCount: number;
}

// SCHEMA FOR PARAMETER
interface Parameter {
  historyTemplateId: string;
  pageIndex: number;
  pageSize: number;
  sortColumn: string;
  sortDirection: string;
  searchValue: string;
}

// DEFAULT PARAMS -- ADD HERE IF YOU NEED NEW PARAMETER
const parameters: Parameter = {
  historyTemplateId: "",
  pageIndex: 1,
  pageSize: PAGE_SIZE,
  sortColumn: "",
  sortDirection: "",
  searchValue: "",
};

// SCHEMA FOR RESULTDATA
interface ResultData {
  historyTemplateColl: Array<any>;
  tabTypeColl: Array<any>;
  totalCount: number;
  totalFinalGrandAmount: number;
}

// DEFAULT VALUES FOR RESULT DATA
const initResultData: ResultData = {
  historyTemplateColl: [],
  tabTypeColl: [],
  totalCount: 0,
  totalFinalGrandAmount: 0,
};

// TABLE ROWS FOR SKELETON
const skeletonRowCount = 5;

// Add centralized error handling utility
const handleError = (error: any, context: any, updateContext: any) => {
  updateContext({
    ...context,
    isAlert: true,
    alertMessage: typeof error === "string" ? error : error?.message,
    alertType: AlertType.Error,
  });
};

// Add API configuration and request cancellation
const createAbortController = () => new AbortController();

const createBaseParams = (userContext: any) => {
  return new URLSearchParams({
    consumerId: userContext.consumerId?.toString() || "",
    organizationId: userContext.organizationId?.toString() || "",
    orgId: userContext.organizationId?.toString() || "",
    branchId: userContext.branchId?.toString() || "",
  });
};

// Add debounce utility
const debounce = (func: Function, wait: number) => {
  let timeout: NodeJS.Timeout;
  return (...args: any[]) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

export default function HistoryTemplateList() {
  // HOOKS AND CONTEXT
  const navigate = useNavigate();
  const theme = useTheme();
  const edService = new EncryptDecryptService();
  const { userContext, updateUserContext } = useContext(PreferencesContext);
  const isSuperAdmin =
    (userContext?.loginDetail?.userRole || "").toString().toLowerCase() ===
    "superadmin";
  // BREAK POINTS
  const smd = useMediaQuery(theme.breakpoints.down("sm"));

  const [param, setParam] = useState({
    ...parameters,
  });

  const [order, setOrder] = React.useState<Order>(""); // DON"T USE  DIFFERENT STATE FOR EVERY PARAMETER USE PARAMETER STATE INSTAND
  const [orderBy, setOrderBy] = React.useState<keyof Data>("none");
  const [rowsPerPage, setRowsPerPage] = React.useState(PAGE_SIZE);
  const [page, setPage] = React.useState(0);
  const [page_Index, setPage_Index] = useState(1);
  const [value, setValue] = useState("");
  const [counData, setCounData] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [pageStartIndex, setPageStartIndex] = useState(1);
  const [pageEndIndex, setPageEndIndex] = useState(1);

  // STATE FOR STORAGE
  const [openDeleteDilog, setOpenDeleteDilog] = React.useState(false);
  const [resultData, setResultData] = useState<ResultData>(initResultData);
  const [selected, setSelected] = useState<readonly number[]>([]);
  const [historyTemplateId, setFollowUpTemplateId] = useState(0);
  const [anchorEl, setAnchorEl3] = React.useState<HTMLButtonElement | null>(
    null
  );

  // STATES -- FOR ERROR HANDLING AND LOADING
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Getting Seach Paramms for Pagination
  function useQuery() {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
  }

  // GETTTING PARAMS
  let urlParams = useQuery();
  let ePage = urlParams.get("page");
  let encodedQuery = urlParams.get("q");

  let query = "";
  let curPage = 0;

  if (
    ePage != null &&
    ePage !== "" &&
    ePage !== undefined &&
    encodedQuery !== null &&
    encodedQuery !== undefined
  ) {
    curPage = +ePage;
    // ENCODE QUERY
    encodedQuery = encodeURIComponent(encodedQuery);

    // DECODE QUERY
    query = decodeURIComponent(encodedQuery);
    // Set paymentMode from URL
  } else {
    navigate("/not-found");
  }

  // HANDLE POPUP FOR EDIT AND DELETE
  const handleOpenMore = (
    event: React.MouseEvent<HTMLButtonElement>,
    historyTemplateId: any
  ) => {
    setAnchorEl3(event.currentTarget);
    setFollowUpTemplateId(historyTemplateId);
  };

  // CLOSE POPUP
  const handleCloseMore = () => {
    setAnchorEl3(null);
  };

  // HANDLE DELETE WARNING POPUP
  const handleDeleteDilog = (value) => {
    setOpenDeleteDilog(value);
  };

  // TABLE HEADER
  function EnhancedTableHead(props: EnhancedTableProps) {
    return (
      <TableHead className="table-header-color">
        <TableRow>
          <TableCell
            sx={{
              fontWeight: "bold",
            }}
            align="left">
            Template Name
          </TableCell>
          <TableCell
            sx={{
              display: {
                xs: "none",
                sm: "table-cell",
              },
              fontWeight: "bold",
            }}
            align="left">
            History Note
          </TableCell>
          <TableCell></TableCell>
        </TableRow>
      </TableHead>
    );
  }

  // SORTING TABLE
  function stableSort<T>(
    array: readonly T[],
    comparator: (a: T, b: T) => number
  ) {
    const stabilizedThis = array?.map(
      (el, index) => [el, index] as [T, number]
    );
    stabilizedThis?.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) {
        return order;
      }
      return a[1] - b[1];
    });
    return stabilizedThis?.map((el) => el[0]);
  }

  // DESCENDING COMPERISION
  function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  }

  // GET COMPERISION
  function getComparator<Key extends keyof any>(
    order: Order,
    orderBy: Key
  ): (
    a: { [key in Key]: number | string },
    b: { [key in Key]: number | string }
  ) => number {
    return order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }

  const [abortController, setAbortController] = useState(
    createAbortController()
  );

  // Update the searchValue function to maintain current date range
  const searchValue = (val: any) => {
    val = val.trim();
    // encode value
    val = encodeURIComponent(val);
    // Maintain current date range in the URL
    navigate(`?page=1&q=${val}`);
  };

  // Update the debouncedSearch to use the current date range
  const debouncedSearch = useCallback(
    debounce((searchTerm: string) => {
      searchValue(searchTerm);
    }, 500),
    []
  );

  // Update the getHistoryTemplateList function to properly handle both search and date
  const getHistoryTemplateList = async () => {
    try {
      abortController.abort();
      const newAbortController = createAbortController();
      setAbortController(newAbortController);

      const baseParams = createBaseParams(userContext);
      // Add pagination, search, and date params
      baseParams.append(
        "departmentId",
        isSuperAdmin ? "0" : userContext?.departmentId?.toString() || "0",
      );
      baseParams.append(
        "specialityId",
        isSuperAdmin ? "0" : userContext?.specialityId?.toString() || "0",
      );
      baseParams.append("pageIndex", param.pageIndex?.toString());
      baseParams.append("pageSize", param.pageSize?.toString());
      baseParams.append("sortColumn", param.sortColumn);
      baseParams.append("sortDirection", param.sortDirection);
      baseParams.append("searchValue", param.searchValue || "");
      baseParams.append("mode", "LIST");

      const result: any = await GetHistoryTemplateList(baseParams);

      if (result?.data?.data?.historyTemplateColl) {
        const resultData: ResultData = result?.data?.data;
        let historyTemplateColl = resultData?.historyTemplateColl || [];

        let dataCount = historyTemplateColl.length;
        let showDataCount = Math.ceil(dataCount / param.pageSize);

        setResultData({
          ...resultData,
          historyTemplateColl: historyTemplateColl,
          totalCount: dataCount, // total after filter
        });

        setCounData(showDataCount);
        setPageStartIndex((page_Index - 1) * param.pageSize + 1);
        setPageEndIndex(
          (page_Index - 1) * param.pageSize + historyTemplateColl?.length
        );
        setTotalCount(dataCount);
      }

      setIsLoading(false);
    } catch (e: any) {
      if (e.name !== "AbortError") {
        setIsLoading(false);
        handleError(e, userContext, updateUserContext);
      }
    }
  };

  // Update the fetchData function to only fetch followUpTemplate list
  const fetchData = async () => {
    try {
      setIsLoading(true);
      await getHistoryTemplateList();
    } catch (e: any) {
      if (e.name !== "AbortError") {
        handleError(e, userContext, updateUserContext);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // ON CHANGE PAGE
  const handleChangePageNew = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    if (curPage !== value) {
      navigate(
        `?page=${value}&q=${encodedQuery}`
      );
    }
  };

  // HANDLE UPDATE
  const handleUpdate = (historyTemplateId: any) => {
    let encrypthistoryTemplatetId: any = edService.encrypt(historyTemplateId);
    navigate(
      {
        pathname: "history-template-save",
        search: `?q=${encodeURIComponent(encrypthistoryTemplatetId)}`,
      },
      {
        state: {
          title: " Edit",
          button: "Update",
          historyTemplateId: historyTemplateId,
        },
      }
    );
  };

  // HANDLE DELETE
  const deleteRow = async (historyTemplateIds: any) => {
    try {
      setAnchorEl3(null);
      handleDeleteDilog(false);
      let DeleteData = {
        historyTemplateIds: historyTemplateIds?.toString(),
        branchId: userContext?.branchId?.toString(),
        organizationId: userContext?.organizationId?.toString(),
        consumerId: userContext?.consumerId?.toString(),
        isDeleted: true,
        dataManagedBy: userContext?.loginDetail?.userId,
      };
      const result: any = await DeleteHistoryTemplate(DeleteData);
      if (result) {
        await getHistoryTemplateList();
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: result.message,
          alertType: AlertType.Success,
        });
      }
    } catch (e) {
      handleError(e, userContext, updateUserContext);
    }
  }

  // HANDLE CHECK ROW
  const handleCheckSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelecteds = resultData?.historyTemplateColl?.map(
        (n: any) => n?.historyTemplateId || 0
      );
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  // Update the clear search functionality
  const handleClearSearch = () => {
    setValue(""); // Clear the input value
    searchValue(""); // Clear the search parameter
  };

  // Update the useEffect for param changes to properly handle both search and date
  useEffect(() => {
    setParam({
      ...param,
      searchValue: query,
      pageIndex: curPage,
    });
  }, [query, curPage]);

  // Update the existing useEffect to only handle param changes
  useEffect(() => {
    fetchData();
  }, [param]); // This will only trigger when param changes

  // Cancel ongoing requests when component unmounts
  useEffect(() => {
    return () => {
      abortController.abort();
    };
  }, []);


  return (
    <>
      <Box>
        <Box className="main-container">
          <Grid container className="page-header">
            <Grid item xs={6}>
              <Box sx={{ display: "flex", height: "100%", alignItems: "end" }}>
                <CustomBreadcrumb />
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box className="page-header-icon">
                <Button
                  color="primary"
                  variant="contained"
                  onClick={() => {
                    handleUpdate(0);
                  }}
                  size="medium"
                  startIcon={<AddIcon />}>
                  Add
                </Button>
              </Box>
            </Grid>
          </Grid>
          <Grid container className="list-container">
            <Grid item xs={12}>
              <Grid item container spacing={2} xs={12}>
                <Grid item xs={12} sm={6} md={3} xl={2}>
                  <SkeletonWrapper
                    isLoading={isLoading}
                    className="skeleton-search-box">
                    <CustomTextField
                      id="searchBox"
                      placeholder="Search..."
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          debouncedSearch(value);
                        }
                      }}
                      type="text"
                      variant="outlined"
                      size="small"
                      className="width-100-percent"
                      onChange={(e) => setValue(e.target.value)}
                      value={value}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <IconButton
                              size="small"
                              onClick={() => debouncedSearch(value)}>
                              <SearchIcon />
                            </IconButton>
                          </InputAdornment>
                        ),
                        endAdornment: value && (
                          <IconButton
                            aria-label="clear search"
                            onClick={handleClearSearch}>
                            <ClearIcon />
                          </IconButton>
                        ),
                      }}
                    />
                  </SkeletonWrapper>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} mt={2}>
              <TableContainer>
                <Table>
                  <EnhancedTableHead
                    numSelected={resultData?.historyTemplateColl?.length || 0}
                    order={order}
                    orderBy={orderBy}
                    onSelectAllClick={handleCheckSelectAll}
                    rowCount={resultData?.historyTemplateColl?.length}
                  />
                  <TableBody>
                    {isLoading ? (
                      Array(skeletonRowCount)
                        ?.fill(0)
                        ?.map((skeletonRow, index) => {
                          return (
                            <TableRow key={"skeleton-row" + index?.toString()}>
                              <TableCell colSpan={8}>
                                <SkeletonWrapper
                                  isLoading={isLoading}
                                  className="skeleton-input">
                                  <span></span>
                                </SkeletonWrapper>
                              </TableCell>
                            </TableRow>
                          );
                        })
                    ) : (
                      <>
                        {stableSort(
                          resultData?.historyTemplateColl,
                          getComparator(order, orderBy)
                        )
                          .slice(
                            page * rowsPerPage,
                            page * rowsPerPage + rowsPerPage
                          )
                          .map((item: any, index: any) => {
                            return (
                              <TableRow
                                key={`examinationTemplate-row-${item?.historyTemplateId || index}`}>
                                <TableCell>
                                  <Link
                                    key={`link-${item?.historyTemplateId}`}
                                    className="text-link"
                                    to={{
                                      pathname: "history-template-save",
                                      search: `?q=${encodeURIComponent(
                                        edService.encrypt(
                                          item?.historyTemplateId || 0
                                        ) || 0
                                      )}`,
                                    }}>
                                    {item?.title}
                                  </Link>
                                </TableCell>
                                <TableCell
                                  sx={{
                                    display: {
                                      xs: "none",
                                      sm: "table-cell",
                                    },
                                  }}>
                                  {parse(item?.note)}
                                </TableCell>
                                <TableCell align="right">
                                  <Tooltip title="More" placement="top">
                                    <IconButton
                                      key={`more-${item?.historyTemplateId}`}
                                      onClick={(e: any) => {
                                        handleOpenMore(e, item?.historyTemplateId || 0);
                                      }}>
                                      <MoreVertRoundedIcon />
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        {resultData?.historyTemplateColl?.length <= 0 && (
                          <TableRow>
                            <TableCell align="center" colSpan={8}>
                              No record(s) found
                            </TableCell>
                          </TableRow>
                        )}
                      </>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
            <Grid item xs={12} paddingTop={1}>
              {resultData?.historyTemplateColl?.length > 0 && (
                <Box className="pagination">
                  <Box className="pagination-label">
                    <SkeletonWrapper
                      isLoading={isLoading}
                      className="skeleton-pagination-label">
                      {pageStartIndex} - {pageEndIndex} of {totalCount}
                    </SkeletonWrapper>
                  </Box>
                  <Box className="pagination-stack">
                    <SkeletonWrapper
                      isLoading={isLoading}
                      className="skeleton-pagination-stack">
                      <Stack component="div">
                        <Pagination
                          color="primary"
                          count={counData} // NUMBER OF PAGES
                          page={curPage} // CURRENT PAGE
                          onChange={handleChangePageNew} //HANDLE PAGE CHANGE
                        />
                      </Stack>
                    </SkeletonWrapper>
                  </Box>
                </Box>
              )}
            </Grid>
          </Grid>
        </Box>
      </Box>

      <Popover
        elevation={1}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleCloseMore}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}>
        <List>
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => {
                handleUpdate(historyTemplateId);
              }}>
              <ListItemText primary="Edit" />
            </ListItemButton>
          </ListItem>
          {userContext.loginDetail.userRole !== "user" && (
            <ListItem disablePadding>
              <ListItemButton
                className="delete-container"
                onClick={() => {
                  handleDeleteDilog(true);
                }}>
                Delete
              </ListItemButton>
            </ListItem>
          )}
        </List>
      </Popover>

      <Dialog
        open={openDeleteDilog}
        onClose={() => {
          handleDeleteDilog(false);
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description">
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this history template?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              deleteRow(historyTemplateId);
            }}
            className="button-delete">
            Yes
          </Button>
          <Button
            onClick={(e) => {
              handleDeleteDilog(false);
            }}
            autoFocus>
            No
          </Button>
        </DialogActions>
      </Dialog>

    </>
  );
}
